import React, { Fragment, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, NakedButton } from 'roo-ui/components';
import { useKey, useLockBodyScroll } from 'react-use';
import { useFocusTrap } from 'hooks/useFocusTrap';
import noop from 'lodash/noop';
import { useGallery } from './useGallery';
import { FullscreenImageWrapper, BoxButton, ThumbnailsWrapper, DisplayMobilePortraitOnly } from './components/primitives';
import { RightNav, LeftNav, TotalImagesText, ImageButton, CloseButton, SkipLink } from './components/GalleryButtons';
import { FullscreenTakeover, Thumbnails, Swipeable } from './components';
import SlidingGallery from './components/SlidingGallery';
import createGlobalState from './createGlobalState';

// TODO: when https://github.com/streamich/react-use/pull/1171 is merged
// we can replaces with react-use version
const useIsMinimised = createGlobalState(true);

const ImageGallery = ({
  images,
  imageSize,
  imageHeight,
  imageWidth,
  children,
  minimisedKeyboardNav,
  onClick,
  onImageChanged,
  uniqueId,
  hideThumbnails,
  imageDescriptionRenderer,
  leftNavRenderer,
  rightNavRenderer,
  ...rest
}) => {
  const [showFullscreen, setShowFullscreen] = useState(false);
  const [minimised, setMinimised] = useIsMinimised();
  const { selected, switchToImage } = useGallery(images);
  const trapRef = useFocusTrap(showFullscreen);

  useEffect(() => {
    setMinimised(!showFullscreen);
  }, [setMinimised, showFullscreen]);

  const handleImageChange = useCallback(
    (id) => {
      switchToImage(id);
      onImageChanged(id);
    },
    [onImageChanged, switchToImage],
  );

  const handleSwitchToNextImage = useCallback(() => handleImageChange(selected.index + 1), [selected, handleImageChange]);
  const handleSwitchToPreviousImage = useCallback(() => handleImageChange(selected.index - 1), [selected, handleImageChange]);

  const handleCloseFullscreen = useCallback(() => setShowFullscreen(false), []);
  const handleOpenFullscreen = useCallback(() => {
    if (children) switchToImage(0);
    if (onClick) onClick();
    setShowFullscreen(true);
  }, [children, onClick, switchToImage]);

  const handleKeyboardNav = (cb) => (event) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if ((minimisedKeyboardNav && minimised) || showFullscreen) cb();
  };

  useKey('ArrowRight', handleKeyboardNav(handleSwitchToNextImage), {}, [selected, showFullscreen, minimised]);
  useKey('ArrowLeft', handleKeyboardNav(handleSwitchToPreviousImage), {}, [selected, showFullscreen, minimised]);
  useKey('Escape', handleCloseFullscreen);
  useLockBodyScroll(showFullscreen);

  const getTitle = () => {
    const { caption, index } = selected;
    const totalImages = images.length;
    const text = caption ? `• ${caption}` : '';
    return `${index + 1} / ${totalImages} ${text}`;
  };

  const uid = `gallery-skip-${uniqueId}`;
  const ImagesText = imageDescriptionRenderer;
  const LeftButton = leftNavRenderer;
  const RightButton = rightNavRenderer;

  return (
    <Fragment>
      {children && (
        <BoxButton as={NakedButton} onClick={handleOpenFullscreen} {...rest}>
          {children}
        </BoxButton>
      )}
      {!children && (
        <Box aria-live="polite" data-testid="image-gallery" {...rest}>
          <Box position="relative" width={imageWidth} height={imageHeight}>
            {!hideThumbnails && <SkipLink href={`#${uid}`}>Skip image gallery</SkipLink>}
            <ImageButton onClick={handleOpenFullscreen}>
              <Swipeable swipeNext={handleSwitchToNextImage} swipePrev={handleSwitchToPreviousImage} width="100%" height="100%">
                <SlidingGallery images={images} selected={selected} height="initial" imageSize={imageSize} />
              </Swipeable>
            </ImageButton>
            {images.length > 1 && (
              <Fragment>
                <LeftButton onClick={handleSwitchToPreviousImage} />
                <RightButton onClick={handleSwitchToNextImage} />
              </Fragment>
            )}
            <ImagesText images={images} selectedIndex={selected.index} />
          </Box>
          {!hideThumbnails && (
            <Thumbnails index={selected.index} images={images} onClick={handleImageChange} width={imageWidth} display={['none', 'block']} />
          )}
          {!hideThumbnails && <div id={uid}></div>}
        </Box>
      )}
      {showFullscreen && (
        <FullscreenTakeover ref={trapRef}>
          <Swipeable
            swipeNext={handleSwitchToNextImage}
            swipePrev={handleSwitchToPreviousImage}
            display="flex"
            width="100%"
            height="100%"
            position="relative"
            flexDirection="column"
          >
            <FullscreenImageWrapper>
              <SlidingGallery images={images} selected={selected} height="100%" imageSize="large" />
              <CloseButton onClick={handleCloseFullscreen} />
              {images.length > 1 && (
                <Fragment>
                  <LeftNav onClick={handleSwitchToPreviousImage} />
                  <RightNav onClick={handleSwitchToNextImage} />
                </Fragment>
              )}
            </FullscreenImageWrapper>
            <ThumbnailsWrapper>
              <Box bg="rgba(34,34,34,0.5)" py={3} px={(3, 6)} color="white">
                {getTitle()}
              </Box>
              {images.length > 1 && (
                <DisplayMobilePortraitOnly>
                  <Thumbnails index={selected.index} images={images} onClick={handleImageChange} />
                </DisplayMobilePortraitOnly>
              )}
            </ThumbnailsWrapper>
          </Swipeable>
        </FullscreenTakeover>
      )}
    </Fragment>
  );
};

ImageGallery.propTypes = {
  children: PropTypes.node,
  images: PropTypes.arrayOf(PropTypes.object).isRequired,
  imageSize: PropTypes.oneOf(['small', 'medium', 'large']).isRequired,
  imageWidth: PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.string]),
  imageHeight: PropTypes.oneOfType([PropTypes.array, PropTypes.number, PropTypes.string]),
  minimisedKeyboardNav: PropTypes.bool,
  onClick: PropTypes.func,
  onImageChanged: PropTypes.func,
  uniqueId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  hideThumbnails: PropTypes.bool,
  imageDescriptionRenderer: PropTypes.func,
  leftNavRenderer: PropTypes.func,
  rightNavRenderer: PropTypes.func,
};

ImageGallery.defaultProps = {
  children: null,
  imageWidth: 600,
  imageHeight: 350,
  minimisedKeyboardNav: true,
  onClick: null,
  onImageChanged: noop,
  uniqueId: 0,
  hideThumbnails: false,
  imageDescriptionRenderer: TotalImagesText,
  leftNavRenderer: LeftNav,
  rightNavRenderer: RightNav,
};

export default ImageGallery;
